import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ShopProduct } from '../../services/apiClient';
import { formatMoney } from '../../utils/money';

export interface ReceiptNotice {
  id: number;
  product: ShopProduct;
  quantity: number;
  subtotalCents: number;
}

interface Props {
  notice: ReceiptNotice | null;
  onDismiss: () => void;
}

const ReceiptStampToast: React.FC<Props> = ({ notice, onDismiss }) => {
  const navigate = useNavigate();
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!notice || paused) return;
    timer.current = window.setTimeout(onDismiss, 5000);
    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    };
  }, [notice, paused, onDismiss]);

  if (!notice) return null;
  const lineTotal = notice.product.retailPriceCents * notice.quantity;

  return (
    <aside
      className="receipt-stamp"
      aria-label="Added to cart"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
      }}
    >
      <span className="receipt-stamp__spine" aria-hidden="true">ADDED</span>
      {notice.product.imageUrl ? (
        <img className="receipt-stamp__thumb" src={notice.product.imageUrl} alt="" />
      ) : (
        <span className="receipt-stamp__thumb receipt-stamp__thumb--blank" aria-hidden="true">2G</span>
      )}
      <div className="receipt-stamp__copy">
        <p className="receipt-stamp__kicker">Filed on the slip</p>
        <p className="receipt-stamp__line">{notice.quantity}× {notice.product.name}</p>
        <p className="receipt-stamp__price">{formatMoney(lineTotal, notice.product.currency)}</p>
        <p className="receipt-stamp__subtotal">Running subtotal <strong>{formatMoney(notice.subtotalCents, notice.product.currency)}</strong></p>
        <div className="receipt-stamp__actions">
          <button type="button" onClick={() => { onDismiss(); navigate('/cart'); }}>View cart</button>
          <button type="button" className="receipt-stamp__checkout" onClick={() => { onDismiss(); navigate('/checkout'); }}>Checkout</button>
        </div>
      </div>
      <button type="button" className="receipt-stamp__close" onClick={onDismiss} aria-label="Dismiss cart confirmation">×</button>
    </aside>
  );
};

export default ReceiptStampToast;
